import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Options for focus management behavior
 */
interface FocusManagementOptions {
  /**
   * Element to focus on route change (defaults to main content area)
   */
  focusTarget?: string;
  /**
   * Delay before focusing (in milliseconds)
   */
  focusDelay?: number;
  /**
   * Whether to announce the page title to screen readers
   */
  announcePageTitle?: boolean;
  /**
   * Skip focus management for certain paths
   */
  skipPaths?: string[];
}

/**
 * Hook to manage focus on route changes for better accessibility
 * Ensures keyboard users are oriented when navigating between pages
 */
export const useFocusManagement = (options: FocusManagementOptions = {}) => {
  const {
    focusTarget = 'main, [role="main"], h1',
    focusDelay = 100,
    announcePageTitle = true,
    skipPaths = [],
  } = options;

  const location = useLocation();
  const previousLocation = useRef(location.pathname);
  const announcerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    // Skip if path hasn't changed or is in skip list
    if (
      location.pathname === previousLocation.current ||
      skipPaths.includes(location.pathname)
    ) {
      return;
    }

    // Update previous location
    previousLocation.current = location.pathname;

    // Focus management after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      // Find the target element to focus
      const targetElement = document.querySelector<HTMLElement>(focusTarget);
      
      if (targetElement) {
        // Make element focusable if it isn't already
        if (!targetElement.hasAttribute('tabindex')) {
          targetElement.setAttribute('tabindex', '-1');
        }
        
        // Focus the element
        targetElement.focus();
        
        // Remove outline on mouse click
        const handleMouseDown = () => {
          targetElement.blur();
          targetElement.removeEventListener('mousedown', handleMouseDown);
        };
        targetElement.addEventListener('mousedown', handleMouseDown);
      }

      // Announce page title to screen readers
      if (announcePageTitle) {
        const pageTitle = document.title;
        announceToScreenReader(`Navigated to ${pageTitle}`);
      }
    }, focusDelay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location, focusTarget, focusDelay, announcePageTitle, skipPaths]);

  /**
   * Announce message to screen readers using ARIA live region
   */
  const announceToScreenReader = (message: string) => {
    if (!announcerRef.current) {
      announcerRef.current = document.createElement('div');
      announcerRef.current.setAttribute('role', 'status');
      announcerRef.current.setAttribute('aria-live', 'polite');
      announcerRef.current.setAttribute('aria-atomic', 'true');
      announcerRef.current.style.position = 'absolute';
      announcerRef.current.style.left = '-10000px';
      announcerRef.current.style.width = '1px';
      announcerRef.current.style.height = '1px';
      announcerRef.current.style.overflow = 'hidden';
      document.body.appendChild(announcerRef.current);
    }

    announcerRef.current.textContent = message;
  };

  return {
    currentPath: location.pathname,
    announceToScreenReader,
  };
};

/**
 * Hook to manage focus within a specific component or region
 */
export const useFocusTrap = (containerRef: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Get all focusable elements within the container
    const getFocusableElements = () => {
      const selectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');
      
      return container.querySelectorAll<HTMLElement>(selectors);
    };

    // Handle Tab key navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift+Tab on first element - focus last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // Tab on last element - focus first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    // Focus the first focusable element
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    // Add event listener
    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [containerRef]);
};

/**
 * Hook to restore focus to a previous element
 */
export const useFocusReturn = () => {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const saveFocus = () => {
    previousFocusRef.current = document.activeElement as HTMLElement;
  };

  const restoreFocus = () => {
    if (previousFocusRef.current && previousFocusRef.current.focus) {
      previousFocusRef.current.focus();
    }
  };

  return {
    saveFocus,
    restoreFocus,
  };
};